import {
  getPatientFailure,
  getPatientLoading,
  getPatientSuccess,
  addPatientLoading,
  addPatientSuccess,
  addPatientFailure,
  findPatientLoading,
  findPatientSuccess,
  findPatientFailure,
  addNewPatientLoading,
  addNewPatientSuccess,
  addNewPatientFailure,
  addCommentLoading,
  addCommentSuccess,
  addCommentFailure,
  getSinglePatientLoading,
  getSinglePatientFailure,
  getSinglePatientSuccess,
  getPatientCountLoading,
  getPatientCountSuccess,
  getPatientCountFailure,
  getReportsLoading,
  getReportsSuccess,
  getReportsFailure,
  addDiagnosisLoading,
  addDiagnosisSuccess,
  addDiagnosisFailure,
  updateDiagnosisLoading,
  updateDiagnosisSuccess,
  updateDiagnosisFailure,
  addReadingFailure,
  addReadingLoading,
  addReadingSuccess,
} from "@redux/slices/patientSlice"
import {
  getPatient,
  addPatient,
  findPatient,
  addNewPatient,
  getPatientCount,
  getReports,
  addDiagnosis,
  updateDiagnosis,
  addReading
} from "@services/patient"
import { notify } from "src/utils/notify"
import getErrorStatement from "src/utils/getErrorStatement"
import { useCallback } from "react"
import { useDispatch } from "react-redux"
import { addComment } from "@services/patient/addComment"

const usePatient = () => {
  const dispatch = useDispatch()

  const handleGetPatient = useCallback(
    async (params = {}) => {
      try {
        dispatch(getPatientLoading())
        // Extract pagination state and filter params
        const { page, limit, ...filterParams } = params;
        const paginationParams = { page, limit };
        // Combine pagination and filter params
        const requestParams = { ...paginationParams, ...filterParams };

        const response = await getPatient(requestParams)
        dispatch(getPatientSuccess(response.data))
        return response.data;
      } catch (error) {
        dispatch(getPatientFailure())
        console.log(error)
        return null;
      }
    },
    [dispatch]
  )

  const handleAddPatient = useCallback(
    async (data, onSuccess) => {
      try {
        dispatch(addPatientLoading())
        const response = await addPatient(data)
        dispatch(addPatientSuccess(response.data))
        notify(response.data.message, "success")
        if (onSuccess) {
          onSuccess()
        }
      } catch (error) {
        dispatch(addPatientFailure())
        notify(getErrorStatement(error))
        console.log("error:", error)
      }
    },
    [dispatch]
  )

  const handleAddNewPatient = useCallback(
    async (data, onSuccess) => {
      try {
        dispatch(addNewPatientLoading())
        const response = await addNewPatient(data)
        dispatch(addNewPatientSuccess(response.data))
        notify(response.data.message, "success")
        if (onSuccess) {
          onSuccess()
        }
      } catch (error) {
        dispatch(addNewPatientFailure())
        notify(getErrorStatement(error))
        console.log("error:", error)
      }
    },
    [dispatch]
  )

  const handleFindPatient = useCallback(
    async (data, onSuccess) => {
      try {
        dispatch(findPatientLoading())
        const response = await findPatient(data)
        dispatch(findPatientSuccess(response.data))
        if (onSuccess) {
          onSuccess(response.data)
        }
      } catch (error) {
        dispatch(findPatientFailure())
        console.log("error:", error)
        throw new Error(getErrorStatement(error))
      }
    },
    [dispatch]
  )

  const handleGetReports = useCallback(
    async (data) => {
      try {
        dispatch(getReportsLoading());
        const response = await getReports(data, { responseType: "blob" });
        dispatch(getReportsSuccess(response.data));
        return response.data;
      } catch (error) {
        dispatch(getReportsFailure());
        console.log("error:", error);
        throw new Error(getErrorStatement(error));
      }
    },
    [dispatch]
  );

  const handleAddComment = useCallback(
    async (data, onSuccess) => {
      try {
        dispatch(addCommentLoading())
        const response = await addComment(data)
        dispatch(addCommentSuccess(response.data))
        if (onSuccess) {
          onSuccess()
        }
      } catch (error) {
        dispatch(addCommentFailure())
        notify(getErrorStatement(error))
        console.log(error)
      }
    },
    [dispatch]
  )

  const handleAddDiagnosis = useCallback(
    async (data, onSuccess) => {
      try {
        dispatch(addDiagnosisLoading())
        const response = await addDiagnosis(data)
        dispatch(addDiagnosisSuccess(response.data))
        if (onSuccess) {
          onSuccess()
        }
      } catch (error) {
        dispatch(addDiagnosisFailure())
        notify(getErrorStatement(error))
        console.log(error)
      }
    },
    [dispatch]
  )

  const handleAddReading = useCallback(
    async (data, onSuccess) => {
      try {
        dispatch(addReadingLoading())
        const response = await addReading(data)
        dispatch(addReadingSuccess(response.data))
        if (onSuccess) {
          onSuccess()
        }
      } catch (error) {
        dispatch(addReadingFailure())
        notify(getErrorStatement(error))
        console.log(error)
      }
    },
    [dispatch]
  )

  const handleUpdateDiagnosis = useCallback(
    async (data, onSuccess) => {
      try {
        dispatch(updateDiagnosisLoading())
        const response = await updateDiagnosis(data)
        dispatch(updateDiagnosisSuccess(response.data))
        if (onSuccess) {
          onSuccess()
        }
      } catch (error) {
        dispatch(updateDiagnosisFailure())
        notify(getErrorStatement(error))
        console.log(error)
      }
    },
    [dispatch]
  )

  const handleGetSinglePatient = useCallback(
    async (data) => {
      try {
        dispatch(getSinglePatientLoading())
        const response = await getPatient(data)
        dispatch(getSinglePatientSuccess(response.data))
      } catch (error) {
        dispatch(getSinglePatientFailure())
        console.log(error)
      }
    },
    [dispatch]
  )

  const handleGetPatientCount = useCallback(
    async (data) => {
      try {
        dispatch(getPatientCountLoading())
        const response = await getPatientCount(data)
        dispatch(getPatientCountSuccess(response.data))
      } catch (error) {
        dispatch(getPatientCountFailure())
        console.log(error)
      }
    },
    [dispatch]
  )

  return {
    handleGetPatient,
    handleAddPatient,
    handleFindPatient,
    handleAddNewPatient,
    handleAddComment,
    handleGetSinglePatient,
    handleGetPatientCount,
    handleGetReports,
    handleAddDiagnosis,
    handleUpdateDiagnosis,
    handleAddReading,
  }
}
export default usePatient
